import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Chip, IconButton, FormHelperText,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Slider, Stack, Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const StoryForm = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pathIdFromQuery = searchParams.get('pathId');
  const isEditMode = storyId !== 'new' && storyId !== undefined;

  // Form state
  const [formValues, setFormValues] = useState({
    title: '',
    content: '',
    path_id: pathIdFromQuery || '',
    difficulty_rating: 3,
    order_index: 0,
    tags: [],
    // Language for initial content (uses path's reference language)
    language_code: 'en'
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [tagDialogOpen, setTagDialogOpen] = useState(false);

  // AI draft generation state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateTheme, setGenerateTheme] = useState('');
  const [generateDirection, setGenerateDirection] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load paths and story data
  useEffect(() => {
    fetchPaths();
    if (isEditMode) {
      fetchStoryData();
    }
  }, [storyId]);

  // Set path details when path is selected
  useEffect(() => {
    if (formValues.path_id && paths.length > 0) {
      const path = paths.find(p => p.path_id === formValues.path_id);
      if (path) {
        setSelectedPath(path);
        // Use path's reference language for initial content
        setFormValues(prev => ({
          ...prev,
          language_code: path.reference_language || 'en'
        }));
      }
    }
  }, [formValues.path_id, paths]);

  // Fetch available paths
  const fetchPaths = async () => {
    try {
      const response = await apiClient.admin.getPaths();
      setPaths(response.data.paths || []);
    } catch (error) {
      console.error('Error fetching paths:', error);
    }
  };

  // Fetch story data for editing
  const fetchStoryData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getStory(storyId);
      const story = response.data.story;

      // Get the first translation or reference language translation
      const referenceTranslation = story.translations?.find(
        t => t.language_code === (story.path?.reference_language || 'en')
      ) || story.translations?.[0];

      setFormValues({
        title: story.title || '',
        content: referenceTranslation?.content || '',
        path_id: story.path_id || '',
        difficulty_rating: story.difficulty_rating || 3,
        order_index: story.order_index || 0,
        tags: story.tags || [],
        language_code: referenceTranslation?.language_code || 'en'
      });
    } catch (error) {
      console.error('Error fetching story:', error);
      showNotification('Failed to load story data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle difficulty rating change
  const handleDifficultyChange = (event, newValue) => {
    setFormValues(prev => ({ ...prev, difficulty_rating: newValue }));
  };

  // Handle tag management
  const handleDeleteTag = (tagToDelete) => {
    setFormValues(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;

    if (!formValues.tags.includes(newTag)) {
      setFormValues(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
    }

    setNewTag('');
    setTagDialogOpen(false);
  };

  // Open the AI generation dialog (seed the theme with the current title)
  const handleOpenGenerate = () => {
    if (!formValues.path_id) {
      showNotification('Select a learning path first so generation matches its level and language', 'error');
      return;
    }
    setGenerateTheme(formValues.title || '');
    setGenerateDirection('');
    setGenerateDialogOpen(true);
  };

  // Generate a draft in the path's reference language and load it into the form.
  // Nothing is saved here — the author reviews/edits, then saves as normal.
  const handleGenerateDraft = async () => {
    if (!generateTheme.trim()) {
      showNotification('Enter a theme or title to generate from', 'error');
      return;
    }

    setGenerating(true);
    try {
      const response = await apiClient.admin.generateStoryDraft({
        path_id: formValues.path_id,
        title: generateTheme,
        description: generateDirection || undefined,
        difficulty_rating: formValues.difficulty_rating
      });

      setFormValues(prev => ({
        ...prev,
        title: prev.title?.trim() ? prev.title : (response.data.title || generateTheme),
        content: response.data.content || prev.content,
        language_code: response.data.language_code || prev.language_code
      }));
      setErrors(prev => ({ ...prev, content: '', title: '' }));
      setGenerateDialogOpen(false);
      showNotification('Draft generated — review and edit before saving', 'success');
    } catch (error) {
      console.error('Error generating draft:', error);
      showNotification(error.response?.data?.error || 'Failed to generate draft', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formValues.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formValues.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formValues.path_id) {
      newErrors.path_id = 'Path is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing story
        await apiClient.admin.updateStory(storyId, {
          title: formValues.title,
          difficulty_rating: formValues.difficulty_rating,
          order_index: formValues.order_index,
          tags: formValues.tags,
          // Update translation content
          content: formValues.content,
          language_code: formValues.language_code
        });

        showNotification('Story updated successfully', 'success');

        // Navigate to translations page
        navigate(`/admin/stories/${storyId}/translations`);
      } else {
        // Create new story with initial content
        const response = await apiClient.admin.createStory({
          ...formValues,
          status: 'draft' // Stories start as draft
        });

        showNotification('Story created successfully', 'success');

        // Navigate to translations page for the new story
        navigate(`/admin/stories/${response.data.story.story_id}/translations`);
      }
    } catch (error) {
      console.error('Error saving story:', error);
      showNotification('Failed to save story', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle navigation back
  const handleBack = () => {
    if (selectedPath) {
      navigate(`/admin/paths/${selectedPath.path_id}`);
    } else {
      navigate('/admin/stories');
    }
  };

  // Show notification
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={handleBack} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isEditMode ? 'Edit Story' : 'Create New Story'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Write the base content — translations and audio come next.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={handleBack}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="story-form"
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {saving ? 'Saving…' : isEditMode ? 'Update & Continue' : 'Create & Add Translations'}
          </Button>
        </Stack>
      </Stack>

      <Paper
        component="form"
        id="story-form"
        onSubmit={handleSubmit}
        variant="outlined"
        sx={{ borderRadius: 3, p: { xs: 2, md: 3 } }}
      >
        <Stack spacing={2.5}>
          {/* Path */}
          <Typography variant="overline" color="text.secondary">
            Learning Path
          </Typography>

          <FormControl fullWidth error={!!errors.path_id} required>
            <InputLabel id="path-label">Learning Path</InputLabel>
            <Select
              labelId="path-label"
              name="path_id"
              value={formValues.path_id}
              onChange={handleInputChange}
              label="Learning Path"
              disabled={isEditMode}
            >
              {paths.map(path => (
                <MenuItem key={path.path_id} value={path.path_id}>
                  {path.title} ({path.level})
                </MenuItem>
              ))}
            </Select>
            {errors.path_id && <FormHelperText>{errors.path_id}</FormHelperText>}
            {selectedPath && (
              <FormHelperText>
                Reference language: {selectedPath.reference_language?.toUpperCase() || 'EN'}
              </FormHelperText>
            )}
          </FormControl>

          {!isEditMode && selectedPath && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Creating story for <strong>{selectedPath.title}</strong> · Level{' '}
              <strong>{selectedPath.level}</strong>
            </Alert>
          )}

          <Divider sx={{ borderColor: 'divider' }} />

          {/* Content */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant="overline" color="text.secondary">
              Story Content
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleOpenGenerate}
              disabled={saving || generating}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Generate with AI
            </Button>
          </Stack>

          <TextField
            fullWidth
            label="Story Title"
            name="title"
            value={formValues.title}
            onChange={handleInputChange}
            error={!!errors.title}
            helperText={errors.title}
            placeholder="Enter an engaging story title"
            required
          />

          <TextField
            fullWidth
            label={`Story Content (${formValues.language_code?.toUpperCase() || 'EN'})`}
            name="content"
            value={formValues.content}
            onChange={handleInputChange}
            error={!!errors.content}
            helperText={errors.content || 'Write your story content here. This will be the base for translations.'}
            multiline
            rows={12}
            required
          />

          <Divider sx={{ borderColor: 'divider' }} />

          {/* Settings */}
          <Typography variant="overline" color="text.secondary">
            Settings
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start">
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography gutterBottom variant="body2" color="text.secondary">
                Difficulty Rating: {formValues.difficulty_rating}
              </Typography>
              <Slider
                value={formValues.difficulty_rating}
                onChange={handleDifficultyChange}
                min={1}
                max={5}
                marks
                valueLabelDisplay="auto"
              />
              <FormHelperText>
                1 = Very Easy, 5 = Very Difficult
              </FormHelperText>
            </Box>

            <TextField
              sx={{ flex: 1, width: '100%' }}
              label="Order in Path"
              name="order_index"
              type="number"
              value={formValues.order_index}
              onChange={handleInputChange}
              helperText="Position of this story in the learning path"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Stack>

          <Divider sx={{ borderColor: 'divider' }} />

          {/* Tags */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {formValues.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
              <Chip
                icon={<AddIcon />}
                label="Add Tag"
                onClick={() => setTagDialogOpen(true)}
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>

        </Stack>
      </Paper>

      {/* AI Generation Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => !generating && setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          Generate Story Draft with AI
        </DialogTitle>
        <DialogContent>
          {selectedPath && (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              Generates in <strong>{(selectedPath.reference_language || 'en').toUpperCase()}</strong> at
              level <strong>{selectedPath.level}</strong>. You can edit the result before saving.
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label="Theme or title"
              placeholder="e.g. A nervous first day at a new job"
              fullWidth
              variant="outlined"
              value={generateTheme}
              onChange={(e) => setGenerateTheme(e.target.value)}
            />
            <TextField
              label="Extra direction (optional)"
              placeholder="e.g. Include a friendly coworker; end on a hopeful note"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={generateDirection}
              onChange={(e) => setGenerateDirection(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setGenerateDialogOpen(false)}
            disabled={generating}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateDraft}
            variant="contained"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={generating}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Tag"
            fullWidth
            variant="outlined"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddTag();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTagDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTag}
            variant="contained"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          elevation={6}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoryForm;
